import React, { useEffect, useMemo } from 'react';
import { useStorm } from '../storm/StormContext';
import { useChaos } from './ChaosContext';
import { pick, range } from './rng';
import './Tear.css';

// Renders the current storm pulse. tear → horizontal static slices sweep across;
// surge → inward collapse vignette; warp → toggles data-storm-warp on <html> so
// elements marked .storm-chrome (frame only, never text) jolt through an SVG filter.
const TearTransition: React.FC = () => {
  const { pulse, reducedMotion } = useStorm();
  const { rngFor } = useChaos();

  const slices = useMemo(() => {
    if (!pulse || pulse.kind !== 'tear') return [];
    const rng = rngFor(`tear-${pulse.id}`);
    const dir = pick(rng, [1, -1] as const);
    const n = 5 + Math.floor(rng() * 5);
    return Array.from({ length: n }, (_, i) => ({
      top: `${range(rng, 0, 100)}%`,
      height: `${range(rng, 4, 22) * pulse.strength}vh`,
      delay: `${Math.round(range(rng, 0, 90))}ms`,
      from: `${dir * range(rng, 30, 110)}%`,
      to: `${-dir * range(rng, 30, 110)}%`,
      key: i,
    }));
  }, [pulse, rngFor]);

  useEffect(() => {
    const el = document.documentElement;
    if (pulse?.kind === 'warp') el.setAttribute('data-storm-warp', '1');
    else el.removeAttribute('data-storm-warp');
  }, [pulse]);

  if (reducedMotion) return null;
  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
        <filter id="storm-warp-filter">
          <feTurbulence type="fractalNoise" baseFrequency="0.012 0.18" numOctaves={1} seed={3} />
          <feDisplacementMap in="SourceGraphic" scale={14} xChannelSelector="R" yChannelSelector="G" />
        </filter>
      </svg>
      {pulse && pulse.kind !== 'warp' && (
        <div className="tear-root" aria-hidden key={pulse.id}>
          {pulse.kind === 'tear' && slices.map((s) => (
            <div key={s.key} className="tear-slice"
              style={{ top: s.top, height: s.height, animationDelay: s.delay, ['--from' as any]: s.from, ['--to' as any]: s.to }} />
          ))}
          {pulse.kind === 'tear' && <div className="tear-flash" />}
          {pulse.kind === 'surge' && <div className="tear-surge" />}
        </div>
      )}
    </>
  );
};

export default TearTransition;
